// callZuordnungen.ts — die Vorgänge/To-dos eines Anrufs lesen (PO 01.10.2026:
// „Ein Anruf kann man mehreren Vorgängen (und mehreren To-dos innerhalb eines
// Vorgangs) zuweisen.").
//
// Die Wahrheit steht in `CallLogDto.zuordnungen`. Ältere Antworten und große
// Listen ohne `mitZuordnungen` bringen das Feld nicht mit — dann gilt der
// Spiegel `vorgangId`/`todoId` (die primäre Zuordnung). Alle Anzeigen lesen
// deshalb über diese Datei, nie direkt über die Spalten.

import type { CallLogDto, CallLogZuordnungRef } from "../../services/telephonyService";

/** Eine Zuordnung; `id` ist `null`, wenn sie nur aus den Spalten abgeleitet ist. */
export type AnrufZuordnung = Omit<CallLogZuordnungRef, "id"> & { id: number | null };

type AnrufMitZuordnung = Pick<CallLogDto, "vorgangId" | "todoId" | "zuordnungen" | "weitereZuordnungenVerborgen">;

/** Alle sichtbaren Zuordnungen, primäre zuerst — mit Spalten-Rückfall. */
export function zuordnungenVon(call: AnrufMitZuordnung | null | undefined): AnrufZuordnung[] {
    if (!call) return [];
    if (Array.isArray(call.zuordnungen)) return call.zuordnungen;
    if (call.vorgangId == null) return [];
    return [
        {
            id: null,
            vorgangId: call.vorgangId,
            vorgangNbr: null,
            vorgangTitel: null,
            todoId: call.todoId ?? null,
            todoTitel: null,
            istPrimaer: true,
        },
    ];
}

/** Zuordnungen in genau diesem Vorgang (Vorgangsebene und To-dos). */
export function zuordnungenImVorgang(call: AnrufMitZuordnung | null | undefined, vorgangId: number): AnrufZuordnung[] {
    return zuordnungenVon(call).filter((z) => z.vorgangId === vorgangId);
}

/** Wie viele Zuordnungen hat der Anruf — sichtbare plus verborgene? */
export function anzahlZuordnungen(call: AnrufMitZuordnung | null | undefined): number {
    if (!call) return 0;
    const sichtbar = zuordnungenVon(call).length;
    // Altbestand: nur ein To-do in der Spalte, ohne Vorgang.
    const nurTodo = sichtbar === 0 && !Array.isArray(call.zuordnungen) && call.todoId != null ? 1 : 0;
    return sichtbar + nurTodo + (call.weitereZuordnungenVerborgen ?? 0);
}

/** Hängt der Anruf an irgendeinem Vorgang oder To-do? */
export function hatZuordnung(call: AnrufMitZuordnung | null | undefined): boolean {
    return anzahlZuordnungen(call) > 0;
}

/** Die Vorgänge des Anrufs (ohne Doppelte). */
export function vorgangIdsVon(call: AnrufMitZuordnung | null | undefined): number[] {
    return Array.from(new Set(zuordnungenVon(call).map((z) => z.vorgangId)));
}

/** „VG-123 · Titel › To-do" — für Chips und die Nach-Anruf-Karte. */
export function zuordnungLabel(z: Pick<AnrufZuordnung, "vorgangId" | "vorgangNbr" | "vorgangTitel" | "todoTitel" | "todoId">): string {
    const vorgang = `${z.vorgangNbr || `#${z.vorgangId}`}${z.vorgangTitel ? ` · ${z.vorgangTitel}` : ""}`;
    if (z.todoId == null) return vorgang;
    return `${vorgang} › ${z.todoTitel || "To-do"}`;
}
